import { Breadcrumb } from "@/components/header/breadcrumbs";
import { useHeaderViewModel } from "@/components/header/header-view-model";
import styles from "@/components/header/header.module.css";
import IconDropdown from "@/components/icon-dropdown/icon-dropdown";
import Loading from "@/components/loading/loading";
import {
  downloadUserManualUsecase,
  fetchHeaderUserInfoUsecase,
  logoutUsecase,
} from "@/init/initializeInstances";
import { UserRole } from "@/utils/enum/user-role";
import {
  EXCLUDE_BREADCRUMB_PATH,
  EXCLUDE_HEADER_PATH,
  PRODUCT_NAME,
} from "@/utils/global-constants";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export default function Header() {
  //MARK:- Properties
  const location = useLocation();
  const pathname = location.pathname;
  // 헤더에서 패스워드 변경 화면으로 이동 할때만 breadcrumb 표시
  const showBreadcrumbFromHeader = Boolean((location.state as any)?.showBreadcrumbFromHeader);
  const { t } = useTranslation();
  const [isMedicalDeviceInfoOpen, setIsMedicalDeviceInfoOpen] = useState(false);

  //MARK:- Functions
  const handleProductNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMedicalDeviceInfoOpen(true);
  };

  const isExcludeHeaderPath = EXCLUDE_HEADER_PATH.includes(pathname);
  const isExcludeBreadcrumbPath =
    !showBreadcrumbFromHeader && EXCLUDE_BREADCRUMB_PATH.some((path) => pathname === path); // || pathname.startsWith(path + "/")

  //MARK:- ViewModel
  const vm = useHeaderViewModel({
    fetchHeaderUserInfoUsecase,
    logoutUsecase,
    downloadUserManualUsecase,
  });

  if (vm.isLoading) {
    return <Loading />;
  }

  //MARK:- Render
  if (isExcludeHeaderPath) {
    console.log("[Header] EXCLUDE_HEADER_PATH 경로에 포함되어 헤더를 표시하지 않습니다.", pathname);
    return null;
  }

  return (
    <header className={styles.header}>
      <div className={styles.headerContainer}>
        <button
          onClick={vm.handleLogoClick}
          className={styles.logoButton}
          aria-label="Go to home page"
        >
          <img
            src="/images/logoSalpha.png"
            alt="Logo"
            width={6.06869 * 16}
            height={2.3125 * 16}
            className={styles.logoImage}
            style={{ width: "auto" }}
          />
          <span
            className={styles.productName}
            onClick={handleProductNameClick}
            style={{ cursor: "pointer" }}
            title="의료기기 정보 보기"
          >
            .{PRODUCT_NAME}
          </span>
        </button>
        <div className={styles.headerRightContainer}>
          {vm.userInfo?.role == UserRole.Doctor && (
            <button
              className={styles.userGuideContainer}
              type="button"
              onClick={vm.handleUserManualDownload}
            >
              <img
                src="/icon/home/question.svg"
                width={1.25 * 16}
                height={1.25 * 16}
                alt="question"
                aria-label="Open User Manual"
              />
              <span className={styles.userGuideText}> {t("header.userManual")}</span>
            </button>
          )}
          <IconDropdown
            positioningMode="css"
            trigger={
              <div className={styles.profileContainer}>
                {vm.userInfo?.role == UserRole.Doctor && (
                  <>
                    <div className={styles.hospitalName}>
                      {vm.userInfo?.hospitalInfo?.name ?? ""} {t("header.hospital")}
                    </div>
                    <div className={styles.userName}>
                      {t("header.doctor", { doctorName: vm.userInfo?.fullName || "" })}
                    </div>
                  </>
                )}
                {vm.userInfo?.role == UserRole.SuperAdmin && (
                  <>
                    <div className={styles.hospitalName}>{t("header.admin")}</div>
                    <div className={styles.userName}>{vm.userInfo?.fullName ?? ""}</div>
                  </>
                )}
                <img
                  src="/icon/home/caret-down.svg"
                  width={1 * 16}
                  height={1 * 16}
                  alt="dropdown"
                  aria-label="Open dropdown"
                />
              </div>
            }
            items={[
              {
                icon: "/icon/home/lock-laminated.svg",
                text: t("header.changePassword"),
                onClick: vm.handlePasswordChange,
              },
              {
                icon: "/icon/home/sign-out.svg",
                text: t("header.logout"),
                onClick: vm.handleLogout,
              },
            ]}
          />
        </div>
      </div>
      {!isExcludeBreadcrumbPath && (
        <Breadcrumb pathname={pathname} userRole={vm.userInfo?.role || null} />
      )}
    </header>
  );
}
